import Info from '@sats-group/icons/16/info';
import AvatarIcon from '@sats-group/icons/160/avatar-subtract';
import Button from '@sats-group/ui-lib/react/button';
import LinkButton from '@sats-group/ui-lib/react/link-button';
import Text from '@sats-group/ui-lib/react/text';
import React, { useEffect, useRef } from 'react';
import BootCampEventHero from '~/client/components/boot-camp-event-hero/boot-camp-event-hero';
import CleanLink from '~/client/components/clean-link/clean-link';
import ContentContainer from '~/client/components/content-container/content-container';
import DynamicImage from '~/client/components/dynamic-image/dynamic-image';
import Layout from '~/client/components/layout/layout';
import type { BootCampEventPage as Props } from './boot-camp-event-page.types';

const BootCampEventPage: React.FunctionComponent<Props> = ({
  actionBar,
  aboutSection,
  dateSection,
  eventDetailsSections,
  layout,
  personalTrainerSection,
}) => {
  const stickyRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sticky = stickyRef.current;
    const cta = ctaRef.current;
    if (!sticky || !cta) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        sticky.classList.toggle('is-visible', !entry?.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(cta);
    return () => observer.disconnect();
  }, []);

  return (
    <Layout {...layout}>
      <BootCampEventHero {...eventDetailsSections} ref={ctaRef} />
      <ContentContainer
        className="boot-camp-event-page"
        theme={ContentContainer.themes.wide}
      >
        <div className="boot-camp-event-page__content">
          <div className="boot-camp-event-page__content-inner">
            {dateSection ? (
              <section className="boot-camp-event-page__section">
                <Text
                  elementName="h3"
                  size={Text.sizes.headline3}
                  theme={Text.themes.emphasis}
                  className="boot-camp-event-page__section-heading"
                >
                  {dateSection.heading}
                </Text>

                <ul className="boot-camp-event-page__dates-list">
                  {dateSection.times.map((date, i) => (
                    <li
                      key={date.date}
                      className="boot-camp-event-page__date-row"
                    >
                      <div className="boot-camp-event-page__date-info">
                        <Text className="boot-camp-event-page__date-counter">
                          S{i + 1}
                        </Text>
                        <Text
                          size={Text.sizes.basic}
                          className="boot-camp-event-page__date"
                        >
                          {date.date}
                        </Text>
                      </div>
                      <Text className="boot-camp-event-page__time">
                        {date.time}
                      </Text>
                    </li>
                  ))}
                </ul>
                <div className="boot-camp-event-page__session-info">
                  <Info />
                  <Text size={Text.sizes.small}>
                    {dateSection.sessionDuration}
                  </Text>
                </div>
              </section>
            ) : null}

            <section className="boot-camp-event-page__section--about">
              {aboutSection?.map((section, index) => (
                <div key={index}>
                  <Text
                    elementName="h3"
                    theme={Text.themes.emphasis}
                    size={Text.sizes.headline3}
                    className="boot-camp-event-page__section-heading"
                  >
                    {section.heading}
                  </Text>
                  <Text>{section.text}</Text>
                </div>
              ))}
            </section>
          </div>

          {personalTrainerSection ? (
            <section className="boot-camp-event-page__section">
              <Text
                elementName="h2"
                size={Text.sizes.headline2}
                theme={Text.themes.emphasis}
              >
                {personalTrainerSection.heading}
              </Text>
              <Text>{personalTrainerSection.text}</Text>

              <div className="boot-camp-event-page__personal-trainer">
                <div className="boot-camp-event-page__intro">
                  <div className="boot-camp-event-page__image">
                    {personalTrainerSection.personalTrainer.image ? (
                      <DynamicImage
                        {...personalTrainerSection.personalTrainer.image}
                        aspectRatio={DynamicImage.aspectRatios.square}
                        theme={DynamicImage.themes.round}
                      />
                    ) : (
                      <AvatarIcon />
                    )}
                  </div>

                  <div>
                    <Text elementName="h3" theme={Text.themes.emphasis}>
                      {personalTrainerSection.personalTrainer.name}
                    </Text>

                    <div className="boot-camp-event-page__meta">
                      {personalTrainerSection.personalTrainer.meta?.map(
                        (info, index) =>
                          index > 0 ? (
                            <div
                              className="boot-camp-event-page__meta-item"
                              key={`${info}-${index}`}
                            >
                              <span className="boot-camp-event-page__dot" />
                              <Text>{info}</Text>
                            </div>
                          ) : (
                            <div key={`${info}-${index}`}>
                              <Text>{info}</Text>
                            </div>
                          )
                      )}
                    </div>
                  </div>
                </div>

                <Text>
                  {personalTrainerSection.personalTrainer.description}
                </Text>
                <div className="boot-camp-event-page__contact">
                  {personalTrainerSection.personalTrainer.email?.email ? (
                    <div className="boot-camp-event-page__contact-item">
                      <Text>
                        {personalTrainerSection.personalTrainer.email?.label}
                      </Text>
                      <CleanLink
                        href={`mailto:${personalTrainerSection.personalTrainer.email?.email}`}
                      >
                        {personalTrainerSection.personalTrainer.email?.email}
                      </CleanLink>
                    </div>
                  ) : null}

                  {personalTrainerSection.personalTrainer.email?.email
                    ? personalTrainerSection.personalTrainer.phoneNumber
                        ?.number && (
                        <span className="boot-camp-event-page__dot" />
                      )
                    : null}

                  {personalTrainerSection.personalTrainer.phoneNumber
                    ?.number ? (
                    <div className="boot-camp-event-page__contact-item">
                      <Text>
                        {
                          personalTrainerSection.personalTrainer.phoneNumber
                            ?.label
                        }
                      </Text>
                      <Text>
                        {
                          personalTrainerSection.personalTrainer.phoneNumber
                            ?.number
                        }
                      </Text>
                    </div>
                  ) : null}
                </div>

                {personalTrainerSection.personalTrainer.profileUrl ? (
                  <div className="boot-camp-event-page__profile-link">
                    <LinkButton
                      variant={LinkButton.variants.secondary}
                      size={LinkButton.sizes.small}
                      {...personalTrainerSection.personalTrainer.profileUrl}
                    />
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}
        </div>
      </ContentContainer>

      <section className="boot-camp-event-page__action" ref={stickyRef}>
        <ContentContainer theme={ContentContainer.themes.wide}>
          <div className="boot-camp-event-page__action-inner">
            <div>
              {actionBar.priceInfo.map(price => (
                <div
                  key={price.price}
                  className="boot-camp-event-page__action-pricing"
                >
                  <Text elementName="span">{price.text}</Text>
                  <Text elementName="span" theme={Text.themes.emphasis}>
                    {price.price} {price.currency.toLowerCase()}
                  </Text>
                </div>
              ))}
            </div>
            <LinkButton {...actionBar.cta} variant={Button.variants.cta} />
          </div>
        </ContentContainer>
      </section>
    </Layout>
  );
};

export default BootCampEventPage;
